// « Ma journée » : ce que le technicien connecté a à faire aujourd'hui, puis les jours suivants.
// Téléphone d'abord (conception § 1.4 et § 1.7) : sur 375 × 667, le chantier, l'itinéraire, le poste et la PREMIÈRE étape sont
// visibles sans défiler ; consignes d'accès, coéquipiers du jour et « Appeler le responsable » suivent.
// Incrément 4 : le numéro de chaque étape du jour devient une case qui ouvre le volet de coche (faite, faite en partie, réalisé
// avec, note ; « Valider » toujours visible) ; la dernière consigne de relève s'affiche en tête du poste ; en fin de carte,
// « Laisser une consigne » et « Déclarer mon temps ». Les jours suivants restent en lecture (rien ne se coche à l'avance).
// Incrément 5 : réserves ouvertes du poste (repliées sur une ligne) ; boutons « Réserve », « Photo », « Mesure », « Pièce posée »
// qui ouvrent leur volet (?volet=…&carte=… pour y revenir directement) ; une étape « photo demandée » propose la photo à la coche.
// Incrément 6 (hors connexion) : « Garder ma journée » garde aussi, pour chaque chantier du jour et du lendemain, sa page, ses réserves,
// ses emplacements et la page du jour ; une saisie faite sans réseau s'affiche tout de suite (« en attente d'envoi ») ; l'en-tête dit
// combien de saisies attendent le réseau (lien vers « Envois »).
import { useEffect, useMemo, useState } from 'react';
import { Link, useSearchParams } from 'react-router';
import { useQuery } from '@tanstack/react-query';
import { AlertTriangle, Camera, Check, CheckCircle2, CircleDashed, Clock, CloudCheck, CloudUpload, FlaskConical, Gauge, MapPin, MessageSquareText, Navigation, Package, Phone, Users } from 'lucide-react';
import { api, type ErreurApi } from '../../../lib/api.ts';
import { useReglagesSalaries, useSession } from '../../../lib/session.tsx';
import { pluriel } from '../../../lib/format.ts';
import { rendreDisponible, useHorsLigne } from '../../../lib/horsLigne.ts';
import { saisies, useFileEnvoi, type OperationFile } from '../../../lib/fileEnvoi.ts';
import { lienTelephone } from '@shared/telephone.ts';
import { Badge, Bouton, Chargement, EtatErreur, EtatVide } from '../../../components/ui.tsx';
import { BoutonHorsLigne } from '../../../components/BoutonHorsLigne.tsx';
import { OngletsInterventions, quantite } from './commun.tsx';
import { CRENEAUX, jourCourt, jourLong, lienItineraire } from './jours.ts';
import { heuresTxt } from './Planning.tsx';
import { DialogueReleve, DialogueTemps, jourHeure, texteCoche, VoletCoche } from './Journal.tsx';
import { estVolet, VoletTerrain, type Volet } from './Terrain.tsx';
import type { AffectationJour, CadreSaisie, EtapeJour, MaJournee, PersonnePlanning, ReleveJour, ReserveOuverte } from './types.ts';

export const URL_MA_JOURNEE = '/api/ma-journee';
const CLE_HORS_LIGNE = 'journee';

/**
 * Ce que « Garder ma journée » met sur le téléphone : la journée, et pour chaque chantier d'aujourd'hui et de demain sa page
 * (postes, étapes, consignes), ses réserves, ses emplacements (volet « Pièce posée ») et la page du jour d'aujourd'hui.
 * Pas les plans ni les documents (ils se gardent à la demande), ni les photos.
 */
export function urlsJournee(j: MaJournee | undefined) {
  const ids = [...new Set((j?.jours.slice(0, 2) ?? []).flatMap((x) => x.affectations.map((a) => a.chantier.id)))];
  return [URL_MA_JOURNEE, ...ids.flatMap((id) => [`/api/chantiers/${id}`, `/api/chantiers/${id}/reserves`, `/api/chantiers/${id}/emplacements`,
    ...(j ? [`/api/chantiers/${id}/jours/${j.aujourdhui}`] : [])])];
}

/** Nom + « Appeler » (téléphone professionnel, rendu par le serveur seulement pour un compte actif). */
function PersonneAppel({ p, detail, libelleAppel }: { p: PersonnePlanning; detail?: string | null; libelleAppel?: string }) {
  return (
    <li className="carnet-personne mj-personne">
      <span className="carnet-nom"><strong>{p.nom}</strong>{detail ? <span className="petit discret"> — {detail}</span> : null}</span>
      {!p.actif && <span className="tres-petit discret">N’est plus dans l’entreprise</span>}
      {p.actif && p.telephone && (
        <a className="btn carnet-appeler" href={lienTelephone(p.telephone)} aria-label={`${libelleAppel ?? 'Appeler'} ${p.nom} (téléphone professionnel ${p.telephone})`}>
          <Phone aria-hidden />{libelleAppel ?? 'Appeler'}</a>
      )}
    </li>
  );
}

function EtatEtape({ statut }: { statut: string }) {
  if (statut === 'fait') return <Badge ton="succes" icone={<CheckCircle2 size={14} aria-hidden />}>Faite</Badge>;
  if (statut === 'en_cours') return <Badge ton="info" icone={<CircleDashed size={14} aria-hidden />}>En cours</Badge>;
  return null;
}

/**
 * Consigne laissée par l'équipe précédente, en tête du poste (jusqu'à la consigne suivante). Repliée sur une ligne (aperçu) pour que
 * la première étape reste visible sans défiler sur téléphone ; touchée, elle s'ouvre en entier.
 */
function Releve({ r, libelle }: { r: ReleveJour; libelle: string }) {
  return (
    <details className="jl-releve" aria-label={`${libelle} de ${r.moi ? 'vous' : r.par}`}>
      <summary>
        <MessageSquareText aria-hidden /><strong className="petit">{libelle} :</strong>
        <span className="jl-releve-apercu petit">{r.texte}</span>
      </summary>
      <p className="petit secondaire">{r.moi ? 'Vous' : r.par}, {jourHeure(r.le)}</p>
      <p>{r.texte}</p>
    </details>
  );
}

/** Réserves ouvertes du poste (et de tout le chantier) : une ligne repliée pour que la première étape reste visible sans défiler. */
function ReservesOuvertes({ liste }: { liste: ReserveOuverte[] }) {
  return (
    <details className="jl-releve mj-reserves" aria-label={`${pluriel(liste.length, 'réserve ouverte', 'réserves ouvertes')}`}>
      <summary>
        <AlertTriangle aria-hidden /><strong className="petit">{pluriel(liste.length, 'réserve ouverte', 'réserves ouvertes')} :</strong>
        <span className="jl-releve-apercu petit">{liste.map((r) => r.texte).join(' ; ')}</span>
      </summary>
      <ul className="mj-reserves-liste">
        {liste.map((r) => (
          <li key={r.id} className="petit"><strong>{r.nature_libelle}</strong>{r.poste ? ` — ${r.poste}` : ' — tout le chantier'}{r.etape ? `, ${r.etape}` : ''} : {r.texte}
            <span className="secondaire"> ({r.moi ? 'vous' : r.par}, {jourHeure(r.le)})</span></li>
        ))}
      </ul>
    </details>
  );
}

/** Cadre de saisie d'une carte : son poste et ses étapes (ou les postes du chantier, sans étapes, si elle n'a pas de poste). */
export const cadreDeCarte = (a: AffectationJour): CadreSaisie => ({
  chantierId: a.chantier.id, posteId: a.poste?.id ?? null, libelle: [a.chantier.reference, a.chantier.title].filter(Boolean).join(' — '),
  postes: a.poste
    ? [{ id: a.poste.id, label: a.poste.label, lieu_id: a.poste.lieu_id, a_poser: a.poste.a_poser,
      etapes: a.etapes.map((e) => ({ id: e.id, label: e.label, photo_required: e.photo_required })) }]
    : a.postes_du_chantier.map((p) => ({ id: p.id, label: p.label, lieu_id: null, a_poser: [], etapes: [] })),
});

export function CarteAffectation({ a, principale, ouvrirCoche, ouvrirVolet, attente = [] }:
  { a: AffectationJour; principale?: boolean; ouvrirCoche?: (e: EtapeJour) => void; ouvrirVolet?: (v: Volet) => void;
    /** Saisies de ce chantier gardées sur le téléphone, pas encore envoyées. */
    attente?: OperationFile[] }) {
  const [volet, setVolet] = useState<null | 'releve' | 'temps'>(null);
  // Réglages de la société (confidentialité des salariés) : photos désactivées = pas de bouton « Photo » (le serveur refuse aussi).
  const { photos } = useReglagesSalaries();
  const itineraire = lienItineraire(a.site.adresse, a.site.ville);
  const adresse = [a.site.adresse, a.site.ville].filter(Boolean).join(', ');
  const cochable = a.peut_cocher && !!ouvrirCoche;
  return (
    <article className="mj-carte" aria-label={`${a.chantier.reference ?? ''} ${a.chantier.title}`}>
      <header className="mj-carte-entete">
        <div className="mj-carte-titre">
          <span className="mj-repere">
            {a.chantier.reference && <span className="mono">{a.chantier.reference}</span>}
            {a.creneau !== 'journee' && <Badge ton="info" icone={false}>{CRENEAUX[a.creneau]}</Badge>}
            {a.chantier.status === 'suspendu' && <Badge ton="ambre" icone={false}>Suspendu</Badge>}
            {a.chantier.demo && <Badge ton="ambre" icone={<FlaskConical size={14} aria-hidden />}>Démo</Badge>}
          </span>
          <Link to={`/interventions/chantiers/${a.chantier.id}`} className="mj-chantier" title={a.chantier.title}>{a.chantier.title}</Link>
        </div>
      </header>
      <div className="mj-lieu">
        <span className="mj-adresse" title={[a.site.nom, adresse].filter(Boolean).join(' — ')}><MapPin aria-hidden /><span className="mj-adresse-texte"><strong>{a.site.nom}</strong>{adresse ? <span className="secondaire"> — {adresse}</span> : null}</span></span>
        {itineraire && (
          <a className={`btn ${principale ? 'btn-principal' : ''} mj-itineraire`} href={itineraire} target="_blank" rel="noopener noreferrer"
            aria-label={`Itinéraire vers ${a.site.nom}`}><Navigation aria-hidden />Itinéraire</a>
        )}
      </div>

      <section className="mj-poste" aria-label="Mon poste">
        <h3 className="mj-poste-titre">
          {a.poste ? a.poste.label : 'Sur le chantier (sans poste précis)'}
          {a.poste?.lieu && <span className="petit discret"> — {a.poste.lieu}</span>}
        </h3>
        {(a.heures || a.note) && (
          <p className="petit secondaire mj-prevu">{a.heures ? `${heuresTxt(a.heures)} prévues` : ''}{a.heures && a.note ? ' — ' : ''}{a.note ?? ''}</p>
        )}
        {a.releve && <Releve r={a.releve} libelle="Relève" />}
        {a.releve_chantier && <Releve r={a.releve_chantier} libelle="Relève (tout le chantier)" />}
        {a.reserves_ouvertes.length > 0 && <ReservesOuvertes liste={a.reserves_ouvertes} />}
        {a.etapes.length > 0 ? (
          <ol className={`mj-etapes ${cochable ? 'mj-etapes-cochables' : ''}`}>
            {a.etapes.map((e, i) => {
              const fait = e.statut === 'fait';
              const derniere = e.coches[e.coches.length - 1];
              // Coche faite sans réseau : l'étape apparaît cochée tout de suite, avec la mention « en attente d'envoi ».
              const enAttente = attente.filter((o) => o.etapeId === e.id && (o.type === 'coche' || o.type === 'annulation'));
              const cocheeIci = enAttente.some((o) => o.type === 'coche' && o.donnees.quantite == null);
              const faitAffiche = fait || cocheeIci;
              return (
                <li key={e.id} className={`mj-etape ${faitAffiche ? 'mj-etape-faite' : ''}`}>
                  {cochable && (
                    <button type="button" className={`mj-case ${fait ? 'est-faite' : cocheeIci ? 'est-en-attente' : e.statut === 'en_cours' ? 'est-en-cours' : ''}`}
                      onClick={() => ouvrirCoche!(e)} aria-label={`${fait ? 'Faite — ouvrir' : cocheeIci ? 'Cochée, en attente d’envoi — ouvrir' : 'Cocher'} l’étape ${i + 1} : ${e.label}`}>
                      {faitAffiche ? <Check aria-hidden /> : <span aria-hidden>{i + 1}</span>}
                    </button>
                  )}
                  <span className="mj-etape-texte">
                    <span className="mj-etape-label">{e.label}{e.photo_required && <Camera size={14} aria-label={e.photos ? 'Photo demandée (prise)' : 'Photo demandée'} className="discret" style={{ marginLeft: 6 }} />}</span>
                    <span className="petit discret chiffre">{quantite(e.qty_planned, e.unit)}{e.hours_planned !== null ? ` — ${heuresTxt(e.hours_planned)}` : ' — temps à renseigner'}
                      {e.statut === 'en_cours' ? ` — fait : ${quantite(e.qty_done, e.unit)}` : ''}</span>
                    {derniere && <span className="petit secondaire">{texteCoche(derniere, e.unit)}{e.coches.length > 1 ? ` (${e.coches.length} coches)` : ''}</span>}
                    {enAttente.length > 0 && (
                      <span className="petit mj-attente"><CloudUpload size={14} aria-hidden />
                        {enAttente.map((o) => (o.type === 'annulation' ? 'Annulation' : o.donnees.quantite != null ? `${o.donnees.quantite} ${e.unit} faits` : 'Cochée')).join(', ')} sur ce téléphone — en attente d’envoi</span>
                    )}
                    {e.quantite_depassee && <span className="petit jl-a-verifier">Quantité déclarée supérieure au prévu — à vérifier</span>}
                    {e.detail && <span className="petit secondaire">{e.detail}</span>}
                  </span>
                  {!enAttente.length && <EtatEtape statut={e.statut} />}
                </li>
              );
            })}
          </ol>
        ) : a.postes_du_chantier.length > 0 ? (
          <ul className="mj-postes">
            {a.postes_du_chantier.map((p) => <li key={p.id}>{p.label} <span className="petit discret">— {pluriel(p.nb_etapes, 'étape')}</span></li>)}
          </ul>
        ) : <p className="petit discret">Aucune étape à afficher.</p>}
        {a.etapes_choisies && <p className="tres-petit discret" style={{ margin: 0 }}>Étapes choisies pour vous ce jour-là ; le poste complet est sur la page du chantier.</p>}
        {cochable && a.etapes.length > 0 && <p className="tres-petit discret" style={{ margin: 0 }}>Touchez le numéro d’une étape pour la cocher (faite ou faite en partie).</p>}
      </section>

      {cochable && ouvrirVolet && (
        <section className="mj-terrain" aria-label="Sur le terrain">
          <Bouton icone={<AlertTriangle aria-hidden />} onClick={() => ouvrirVolet('reserve')}>Réserve</Bouton>
          {photos && <Bouton icone={<Camera aria-hidden />} onClick={() => ouvrirVolet('photo')}>Photo</Bouton>}
          <Bouton icone={<Gauge aria-hidden />} onClick={() => ouvrirVolet('mesure')}>Mesure</Bouton>
          <Bouton icone={<Package aria-hidden />} onClick={() => ouvrirVolet('piece')}>Pièce posée</Bouton>
        </section>
      )}
      {(() => {
        // Autres saisies de ce chantier gardées sur le téléphone (consigne, temps, réserve, mesure, pièce, photo).
        const autres = attente.filter((o) => o.type !== 'coche' && o.type !== 'annulation');
        return autres.length > 0 ? (
          <p className="petit mj-attente" style={{ margin: 0 }}><CloudUpload size={14} aria-hidden />
            {saisies(autres.length)} de ce chantier en attente d’envoi : {autres.map((o) => o.libelle.split(' : ')[0]).slice(0, 3).join(', ')}{autres.length > 3 ? '…' : ''}</p>
        ) : null;
      })()}
      {cochable && (
        <section className="mj-fin" aria-label="Fin de journée">
          <Bouton icone={<MessageSquareText aria-hidden />} onClick={() => setVolet('releve')}>Laisser une consigne</Bouton>
          {a.temps_ici && (
            <Bouton icone={<Clock aria-hidden />} onClick={() => setVolet('temps')}>
              {a.mon_temps ? `Mon temps : ${heuresTxt(a.mon_temps.heures)}` : 'Déclarer mon temps'}</Bouton>
          )}
          <Link className="btn btn-fantome" to={`/interventions/chantiers/${a.chantier.id}/jour/${a.jour}`}>Page du jour</Link>
        </section>
      )}
      {volet === 'releve' && <DialogueReleve a={a} fermer={() => setVolet(null)} />}
      {volet === 'temps' && <DialogueTemps a={a} fermer={() => setVolet(null)} />}

      {a.chantier.consignes_acces && (
        <section className="mj-acces" aria-label="Consignes d’accès">
          <strong>Consignes d’accès</strong>
          <p>{a.chantier.consignes_acces}</p>
        </section>
      )}

      <section className="mj-equipe" aria-label="Avec vous ce jour-là">
        <h3 className="mj-sous-titre"><Users aria-hidden />{a.coequipiers.length ? 'Avec vous ce jour-là' : 'Seul ce jour-là'}</h3>
        {a.coequipiers.length > 0 && (
          <ul className="carnet-qui">
            {a.coequipiers.map((p) => <PersonneAppel key={p.id} p={p} detail={[p.poste, p.creneau !== 'journee' ? CRENEAUX[p.creneau] : null].filter(Boolean).join(', ') || null} />)}
          </ul>
        )}
        {a.responsable && (
          <>
            <h3 className="mj-sous-titre">Responsable du chantier</h3>
            <ul className="carnet-qui"><PersonneAppel p={a.responsable} libelleAppel="Appeler le responsable" /></ul>
          </>
        )}
        {a.vous_etes_responsable && <p className="petit secondaire" style={{ margin: 0 }}>Vous êtes le responsable de ce chantier.</p>}
      </section>
    </article>
  );
}

export function PageMaJournee() {
  const { moi } = useSession();
  const q = useQuery({ queryKey: ['ma-journee'], queryFn: () => api.get<MaJournee>(URL_MA_JOURNEE) });
  const horsLigne = useHorsLigne();
  const file = useFileEnvoi();
  const urls = useMemo(() => urlsJournee(q.data), [q.data]);
  const garde = horsLigne.estDisponible(CLE_HORS_LIGNE);
  // Volet de coche : ouvert par le numéro d'une étape, ou par l'adresse (?cocher=<étape>) pour y revenir directement.
  const [params, setParams] = useSearchParams();
  const cible = params.get('cocher');
  const carte = params.get('carte');
  // Étape d'une carte du JOUR que l'on peut cocher (la carte précisée d'abord, sinon la première qui contient l'étape).
  const ouvert = useMemo(() => {
    if (!cible || !q.data) return null;
    const cartes = (q.data.jours[0]?.affectations ?? []).filter((a) => a.peut_cocher);
    for (const a of [...cartes.filter((x) => x.id === carte), ...cartes]) {
      const e = a.etapes.find((x) => x.id === cible);
      if (e) return { a, e };
    }
    return null;
  }, [cible, carte, q.data]);
  const ouvrirCoche = (a: AffectationJour) => (e: EtapeJour) => { const n = new URLSearchParams(params); n.set('cocher', e.id); n.set('carte', a.id); setParams(n); };
  const fermerCoche = () => { const n = new URLSearchParams(params); n.delete('cocher'); n.delete('carte'); setParams(n, { replace: true }); };
  // Volets « Réserve », « Photo », « Mesure », « Pièce posée » (incrément 5) : ?volet=…&carte=<affectation> (la première carte cochable sinon).
  const brutVolet = params.get('volet');
  const voletOuvert = useMemo(() => {
    if (!estVolet(brutVolet) || !q.data || (brutVolet === 'photo' && q.data.reglages?.photos === false)) return null;
    const cartes = (q.data.jours[0]?.affectations ?? []).filter((a) => a.peut_cocher);
    const a = cartes.find((x) => x.id === carte) ?? cartes[0];
    return a ? { volet: brutVolet, a } : null;
  }, [brutVolet, carte, q.data]);
  const ouvrirVolet = (a: AffectationJour) => (v: Volet) => { const n = new URLSearchParams(params); n.set('volet', v); n.set('carte', a.id); setParams(n); };
  const fermerVolet = () => { const n = new URLSearchParams(params); n.delete('volet'); n.delete('carte'); setParams(n, { replace: true }); };
  // Gardée sur ce téléphone : rafraîchie à chaque ouverture avec du réseau (même durée et même effacement que le reste).
  useEffect(() => {
    if (!q.data || !navigator.onLine || !horsLigne.estDisponible(CLE_HORS_LIGNE)) return;
    rendreDisponible({ cle: CLE_HORS_LIGNE, type: 'journee', libelle: 'Ma journée (chantiers)', urls }).catch(() => {});
  }, [q.dataUpdatedAt, horsLigne.liste.length]);   // eslint-disable-line react-hooks/exhaustive-deps

  const horsConnexion = (q.error as ErreurApi | null)?.code === 'hors_connexion';
  const aujourdhui = q.data?.jours[0];
  const suivants = q.data?.jours.slice(1) ?? [];
  return (
    <div className="pile mj-page">
      <header className="mj-entete">
        <div className="pile" style={{ gap: 0, minWidth: 0 }}>
          <h1 className="mj-h1">Ma journée</h1>
          <span className="secondaire mj-date">{q.data ? jourLong(q.data.aujourdhui) : ' '}<span className="masque-etroit"> — {moi.utilisateur.nom}</span></span>
        </div>
        {/* État de la journée sur ce téléphone (conception § 1.4) : saisies en attente d'envoi, ou disponible hors connexion. */}
        {file.ops.length > 0 ? (
          <Link to="/interventions/envois" className={`btn btn-compact mj-etat ${file.refusees.length ? 'mj-etat-refus' : ''}`}>
            <CloudUpload aria-hidden />{file.refusees.length ? `${file.refusees.length} refusée${file.refusees.length > 1 ? 's' : ''}` : `${file.enAttente.length} en attente`}
            <span className="masque-etroit">{file.refusees.length ? ' — à voir' : ' d’envoi'}</span></Link>
        ) : garde ? <span className="petit secondaire mj-etat-garde"><CloudCheck size={16} aria-hidden /><span className="masque-etroit">Disponible hors connexion</span><span className="visible-etroit">Gardée</span></span> : null}
      </header>
      <OngletsInterventions />
      {q.isLoading && <Chargement lignes={4} />}
      {q.isError && (horsConnexion
        ? <EtatVide titre="Hors connexion" texte="Votre journée n’a pas été gardée sur ce téléphone. Avec du réseau, touchez « Garder hors connexion » en haut de cette page." />
        : <EtatErreur erreur={q.error} reessayer={() => q.refetch()} />)}
      {aujourdhui && (aujourdhui.affectations.length === 0 ? (
        <EtatVide titre="Rien de prévu pour vous aujourd’hui"
          texte={suivants.length ? 'Vos prochains jours de chantier sont ci-dessous.' : 'Aucune affectation sur les deux prochaines semaines. Le planning se fait depuis la page du chantier (onglet Planning).'} />
      ) : (
        <div className="pile mj-aujourdhui">
          {aujourdhui.affectations.length > 1 && <p className="petit secondaire" style={{ margin: 0 }}>{pluriel(aujourdhui.affectations.length, 'affectation')} aujourd’hui, dans l’ordre.</p>}
          {aujourdhui.affectations.map((a, i) => <CarteAffectation key={a.id} a={a} principale={i === 0} ouvrirCoche={ouvrirCoche(a)} ouvrirVolet={ouvrirVolet(a)}
            attente={file.enAttente.filter((o) => o.chantierId === a.chantier.id)} />)}
        </div>
      ))}
      {ouvert && <VoletCoche key={`${ouvert.a.id}-${ouvert.e.id}`} a={ouvert.a} etape={ouvert.e} fermer={fermerCoche} />}
      {voletOuvert && !ouvert && <VoletTerrain key={`${voletOuvert.a.id}-${voletOuvert.volet}`} volet={voletOuvert.volet} cadre={cadreDeCarte(voletOuvert.a)} fermer={fermerVolet} />}
      {q.data && (
        <div className="mj-hors-ligne">
          <span className="petit secondaire">{garde
            ? 'Journée gardée sur ce téléphone : consultable sans réseau ; vos saisies partent seules au retour du réseau.'
            : 'Sous-sol, toiture sans réseau : gardez votre journée sur ce téléphone avant de partir.'}</span>
          <BoutonHorsLigne cle={CLE_HORS_LIGNE} type="journee" libelle="Ma journée (chantiers)" urls={urls}
            textes={{ garder: 'Garder ma journée', garde: 'Journée gardée' }} />
        </div>
      )}
      {suivants.length > 0 && (
        <section className="pile" aria-label="Les jours suivants">
          <h2 className="mj-h2">Les jours suivants</h2>
          {suivants.map((j) => (
            <details key={j.jour} className="mj-jour">
              <summary>
                <span className="mj-jour-nom">{jourCourt(j.jour)}</span>
                <span className="mj-jour-resume">{j.affectations.map((a) => `${a.chantier.reference ?? a.chantier.title}${a.poste ? ` — ${a.poste.label}` : ''}`).join(' ; ')}</span>
              </summary>
              <div className="pile">{j.affectations.map((a) => <CarteAffectation key={a.id} a={a} />)}</div>
            </details>
          ))}
        </section>
      )}
    </div>
  );
}
